/**
 * Conflict Panel (Epic C-4 — Bezetting & conflicten, Story C-4.3, UX-C5)
 *
 * Van-rechts-inschuivend custom paneel — GEEN shadcn/Radix Sheet (dit
 * project heeft geen `@radix-ui/*`-dependency, AD-18): overlay +
 * Escape-to-close, zelfde precedent als `UitDienstDialog.tsx`, hier
 * rechts-vast in plaats van gecentreerd.
 *
 * Toont conflictkaarten (nieuwste boven, gesorteerd op `since`) en
 * kwalificatie-waarschuwingen onder een aparte amber sectiekop — zelfde
 * acties (verwijderen/aanpassen), visueel onderscheiden van rood.
 */
import { useEffect } from 'react';
import type { PlanningConflict, PlanningDayPart, QualificationWarningItem } from '@functieloonhuis/shared';

export interface ConflictItemRef {
  employeeId: number;
  date: string;
  dayPart: PlanningDayPart;
  taskId: number;
}

interface ConflictPanelProps {
  isOpen: boolean;
  onClose: () => void;
  conflicts: PlanningConflict[];
  qualificationWarnings: QualificationWarningItem[];
  onRemove: (item: ConflictItemRef) => void;
  onAdjust: (item: ConflictItemRef) => void;
}

function formatDatumDagdeel(date: string, dayPart: PlanningDayPart): string {
  return `${date} — ${dayPart}`;
}

function formatSince(since: string | null): string {
  if (!since) return 'onbekend';
  return new Date(since).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
}

export function ConflictPanel({ isOpen, onClose, conflicts, qualificationWarnings, onRemove, onAdjust }: ConflictPanelProps) {
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sortedConflicts = [...conflicts].sort((a, b) => {
    if (a.since === null && b.since === null) return 0;
    if (a.since === null) return 1;
    if (b.since === null) return -1;
    return b.since.localeCompare(a.since);
  });

  const isEmpty = sortedConflicts.length === 0 && qualificationWarnings.length === 0;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" data-testid="conflict-panel-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Conflicten"
        data-testid="conflict-panel"
        className="h-full w-full max-w-md bg-white shadow-xl overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#1E293B]">Conflicten</h2>
          <button type="button" data-testid="conflict-panel-close" onClick={onClose} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600">
            &#10005;
          </button>
        </div>

        {isEmpty && (
          <div data-testid="conflict-panel-leeg" className="flex items-center gap-2 rounded-md bg-[#B7D945]/15 border border-[#B7D945]/40 px-3 py-3 text-sm text-[#1E293B]">
            <span aria-hidden="true" style={{ color: '#3F6212' }}>
              &#10003;
            </span>
            Geen conflicten — alles werkbaar.
          </div>
        )}

        {sortedConflicts.length > 0 && (
          <ul aria-label="Conflicten" className="space-y-2 mb-6">
            {sortedConflicts.map((conflict) => {
              const key = `${conflict.employeeId}-${conflict.date}-${conflict.dayPart}`;
              return (
                <li
                  key={key}
                  data-testid={`conflict-kaart-${key}`}
                  className="rounded-md border px-3 py-2.5 text-sm"
                  style={{ borderColor: '#D6454540', backgroundColor: '#D6454508' }}
                >
                  <div className="flex items-center justify-between">
                    <span data-testid="conflict-kaart-naam" className="font-medium text-[#1E293B]">
                      {conflict.employeeName}
                    </span>
                    <span className="text-xs text-gray-500">{formatSince(conflict.since)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span aria-hidden="true" className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: conflict.taskColor ?? '#E2E8F0' }} />
                    <span className="text-[#1E293B]">{conflict.taskName}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-0.5">{formatDatumDagdeel(conflict.date, conflict.dayPart)}</p>
                  <p className="text-xs mt-1" style={{ color: '#D64545' }}>
                    {conflict.cause}
                  </p>
                  <div className="flex justify-end gap-3 mt-2">
                    <button
                      type="button"
                      data-testid={`conflict-card-aanpassen-${key}`}
                      onClick={() => {
                        onAdjust({ employeeId: conflict.employeeId, date: conflict.date, dayPart: conflict.dayPart, taskId: conflict.taskId });
                        onClose();
                      }}
                      className="text-xs font-medium text-[#54949E] hover:underline"
                    >
                      Aanpassen in planbord
                    </button>
                    <button
                      type="button"
                      data-testid={`conflict-card-verwijderen-${key}`}
                      onClick={() =>
                        onRemove({ employeeId: conflict.employeeId, date: conflict.date, dayPart: conflict.dayPart, taskId: conflict.taskId })
                      }
                      className="text-xs font-medium hover:underline"
                      style={{ color: '#D64545' }}
                    >
                      Indeling verwijderen
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {qualificationWarnings.length > 0 && (
          <>
            <h3 className="text-sm font-semibold mb-2" style={{ color: '#B45309' }}>
              Kwalificatie ontbreekt
            </h3>
            <ul aria-label="Kwalificatie-waarschuwingen" className="space-y-2">
              {qualificationWarnings.map((warning) => {
                const key = `${warning.employeeId}-${warning.date}-${warning.dayPart}`;
                return (
                  <li
                    key={key}
                    data-testid={`kwalificatie-kaart-${key}`}
                    className="rounded-md border px-3 py-2.5 text-sm"
                    style={{ borderColor: '#FDE68A', backgroundColor: '#FFFBEB' }}
                  >
                    <span className="font-medium text-[#1E293B]">{warning.employeeName}</span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span aria-hidden="true" className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: warning.taskColor ?? '#E2E8F0' }} />
                      <span className="text-[#1E293B]">{warning.taskName}</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">{formatDatumDagdeel(warning.date, warning.dayPart)}</p>
                    <div className="flex justify-end gap-3 mt-2">
                      <button
                        type="button"
                        data-testid={`conflict-card-aanpassen-${key}`}
                        onClick={() => {
                          onAdjust({ employeeId: warning.employeeId, date: warning.date, dayPart: warning.dayPart, taskId: warning.taskId });
                          onClose();
                        }}
                        className="text-xs font-medium text-[#54949E] hover:underline"
                      >
                        Aanpassen in planbord
                      </button>
                      <button
                        type="button"
                        data-testid={`conflict-card-verwijderen-${key}`}
                        onClick={() =>
                          onRemove({ employeeId: warning.employeeId, date: warning.date, dayPart: warning.dayPart, taskId: warning.taskId })
                        }
                        className="text-xs font-medium hover:underline"
                        style={{ color: '#B45309' }}
                      >
                        Indeling verwijderen
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
